import { TappIcon } from '@/components/ui/TappIcon';
import { TappInputError } from '@/components/ui/TappInputError';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappPill } from '@/components/ui/TappPill';
import { cn } from '@/components/ui/utils';
import dayjs from 'dayjs';
import { useState } from 'react';
import { ControllerFieldState } from 'react-hook-form';
import Select, {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  OptionsOrGroups,
  Props,
  ValueContainerProps,
} from 'react-select';

export type TappSelectOptionValue = string | number | boolean | Date;
export type TappSelectOption = { label: string; value: TappSelectOptionValue };

export type TappSelectProps<
  Option extends TappSelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = {
  border?: boolean;
  isSubmitted?: boolean;
} & Props<Option, IsMulti, Group> &
  Partial<ControllerFieldState>;

export const findOptionByValue = <Option extends TappSelectOption, Group extends GroupBase<Option>>(
  searchValue: TappSelectOptionValue,
  options: OptionsOrGroups<Option, Group>
) => {
  const isOptionSame = (option: Option, value: TappSelectOptionValue) => {
    if (value instanceof Date) {
      if (option.value instanceof Date || typeof option.value === 'string') {
        return dayjs(option.value).isSame(value);
      }
      return false;
    }
    return option.value === value;
  };

  for (const option of options) {
    if ('options' in option) {
      const foundInSublist = option.options.find((o) => isOptionSame(o, searchValue));
      if (foundInSublist) return foundInSublist;
    } else if (isOptionSame(option, searchValue)) {
      return option;
    }
  }

  return null;
};

function TappSelect<
  Option extends TappSelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  className,
  isClearable = true,
  error,
  invalid,
  isDirty,
  isTouched,
  border = true,
  isSubmitted,
  ...props
}: TappSelectProps<Option, IsMulti, Group>) {
  const [uniqueId] = useState(() => 'select_' + Math.random().toFixed(5).slice(2));

  const hasErrorVisible = (isTouched || isSubmitted) && invalid && error;

  const closeMenuHandler = () => {
    // Handle the animation on menu close
    // https://stackoverflow.com/a/63548097/5956356
    const menuEl = document.querySelector(`#${uniqueId} .menu`);
    const containerEl = menuEl?.parentElement;
    const clonedMenuEl = menuEl?.cloneNode(true) as HTMLElement | undefined;

    if (!clonedMenuEl) return; // safeguard

    clonedMenuEl.classList.add('animate-fadeOut');
    clonedMenuEl.addEventListener('animationend', () => {
      containerEl?.removeChild(clonedMenuEl);
    });

    containerEl?.appendChild(clonedMenuEl!);
  };

  return (
    <div className={cn('group flex flex-shrink flex-col', className)}>
      <Select
        id={uniqueId}
        className={cn(className)}
        isClearable={isClearable}
        onMenuClose={() => closeMenuHandler()}
        closeMenuOnSelect={props.isMulti ? false : true}
        hideSelectedOptions={false}
        components={{ DropdownIndicator, ClearIndicator, ValueContainer }}
        menuPortalTarget={document.body}
        noOptionsMessage={() => 'Nicio opțiune disponibilă'}
        placeholder={props.placeholder || 'Selectează...'}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        classNames={{
          control: ({ isDisabled, isFocused, menuIsOpen }) =>
            cn('h-10 !rounded border bg-background shadow-none', {
              '!border-border': border && !hasErrorVisible,
              '!border-transparent': !border && !hasErrorVisible,
              'hover:!bg-accent': !isFocused,
              '!border-primary': isFocused && (!hasErrorVisible || menuIsOpen),
              '!border-destructive': hasErrorVisible && !menuIsOpen,
              '!text-muted-foreground !bg-muted': isDisabled,
            }),
          singleValue: () => cn('text-foreground'),
          placeholder: () => cn('text-muted-foreground'),
          valueContainer: ({ isDisabled }) => cn('px-4 py-0 text-sm font-medium'),
          indicatorsContainer: () => cn('pr-3'),
          indicatorSeparator: () => cn('hidden'),
          dropdownIndicator: () => cn('p-0 h-full w-5 flex items-center cursor-pointer'),
          clearIndicator: () => cn('p-0 h-full w-5 flex items-center cursor-pointer mr-2'),
          menu: ({}) =>
            cn('my-[2px] border border-border rounded bg-background shadow-md py-1 animate-fadeIn origin-top menu'),
          option: ({ isDisabled, isFocused, isSelected }) =>
            cn('relative flex min-h-8 cursor-pointer items-center pl-4 pr-4 text-sm transition font-medium leading-5', {
              '!bg-primary !text-primary-foreground': isSelected && !isDisabled,
              '!bg-accent': !isSelected && isFocused && !isDisabled,
              '!text-foreground hover:!bg-accent': !isSelected && !isDisabled,
              '!text-muted-foreground !bg-muted': isDisabled,
            }),
          noOptionsMessage: () => cn('text-sm py-1'),
        }}
        {...props}
      />

      <TappInputError className="mt-1">{hasErrorVisible && error.message}</TappInputError>
    </div>
  );
}

TappSelect.displayName = 'TappSelect';
export { TappSelect };

function DropdownIndicator<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: DropdownIndicatorProps<Option, IsMulti, Group>) {
  return (
    <components.DropdownIndicator {...props}>
      <TappIcon
        size={20}
        className={cn('text-muted-foreground transition hover:text-gray-600', {
          'rotate-180': props.selectProps.menuIsOpen,
        })}
      >
        expand_more
      </TappIcon>
    </components.DropdownIndicator>
  );
}

function ClearIndicator<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: ClearIndicatorProps<Option, IsMulti, Group>
) {
  return (
    <components.ClearIndicator {...props}>
      <TappIcon size={16} className={cn('text-muted-foreground transition hover:text-gray-600')}>
        clear
      </TappIcon>
    </components.ClearIndicator>
  );
}

function ValueContainer<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>({
  children,
  ...props
}: ValueContainerProps<Option, IsMulti, Group>) {
  if (!props.hasValue || !props.isMulti) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const [chips, otherChildren] = children as any;
  const firstChip = props.getValue()[0] as any;

  return (
    <components.ValueContainer {...props}>
      {chips.length === 1 ? (
        <TappPill className="mr-1">{firstChip?.label ?? firstChip}</TappPill>
      ) : (
        <TappPill className="mr-1">{chips.length} items</TappPill>
      )}
      {otherChildren}
    </components.ValueContainer>
  );
}
